import React, {useEffect, useState, useMemo, useCallback} from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import ButtonAtm from "../../../../atomic/Button/Button";
import {useTranslation} from "react-i18next";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {CircularProgress, Grid, Box, Chip, Paper, Select, MenuItem, FormControl, InputLabel} from "@material-ui/core";
import {getProperty, isDefined} from "../../../../../utils";
import {useDispatch, useSelector} from "react-redux";
import {Alert} from "@material-ui/lab";
import {
    getCpaClearState,
    getCpaDailyPrice, getPropertyCpa,
    setSyncCpaCompetitor,
    setSyncCpaCompetitorClearState
} from "../../../../../redux/actions/Network/Inventory/CompetitorActions";
import {
    getCpaGroup,
    syncCpaGroup,
    getCpaGroupMapping,
    syncCpaGroupMapping,
} from "../../../../../redux/actions/Network/Inventory/CpaGroupActions";

const styles = theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(3),
        background: '#0286ff',
        color: '#fff',
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: '#fff',
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
        }
    },
});

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: 16,
            overflow: 'hidden',
            maxWidth: 700,
            width: '100%',
        }
    },
    dialogContent: {
        padding: theme.spacing(3),
        backgroundColor: '#fafafa',
    },
    infoAlert: {
        borderRadius: 12,
        marginBottom: 20,
        backgroundColor: '#e3f2fd',
        border: '1px solid #90caf9',
        '& .MuiAlert-icon': { color: '#1976d2' },
        '& .MuiAlert-message': { color: '#1565c0', fontWeight: 500 }
    },
    successAlert: {
        borderRadius: 12,
        marginBottom: 16,
        backgroundColor: '#e8f5e9',
        border: '1px solid #a5d6a7',
    },
    errorAlert: {
        borderRadius: 12,
        marginBottom: 16,
        backgroundColor: '#ffebee',
        border: '1px solid #ef9a9a',
    },
    autocompleteWrapper: {
        marginBottom: 24,
    },
    autocomplete: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#fff',
            transition: 'all 0.2s ease',
            '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
            '&.Mui-focused': { boxShadow: '0 4px 12px rgba(103, 126, 234, 0.2)' },
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
        '& .MuiInputLabel-outlined': { fontWeight: 500 },
    },
    chip: {
        margin: 2,
        borderRadius: 8,
        backgroundColor: '#e3f2fd',
        fontWeight: 500,
    },
    hotelOption: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 4px',
    },
    submitButton: {
        borderRadius: 12,
        padding: '14px 32px',
        fontWeight: 700,
        fontSize: 15,
        textTransform: 'none',
        boxShadow: '0 4px 12px rgba(215, 31, 38, 0.3)',
        transition: 'all 0.2s ease',
        '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 16px rgba(215, 31, 38, 0.4)' },
        '&:disabled': { backgroundColor: '#ccc', boxShadow: 'none' }
    },
    selectedCount: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderRadius: 12,
        marginBottom: 16,
        border: '1px solid #e0e0e0',
    },
    countBadge: {
        backgroundColor: '#d71f26',
        color: '#fff',
        borderRadius: 20,
        padding: '4px 12px',
        fontWeight: 700,
        fontSize: 13,
    },
    groupSection: {
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        border: '1px solid #e0e0e0',
    },
    groupSelectControl: {
        width: '100%',
        marginBottom: 12,
        '& .MuiOutlinedInput-root': { borderRadius: 12, backgroundColor: '#fff' },
    },
    newGroupInput: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
        padding: '12px 16px',
        backgroundColor: '#f0f7ff',
        borderRadius: 12,
        border: '1px dashed #90caf9',
    },
    groupMappingChip: {
        margin: 2,
        borderRadius: 8,
        backgroundColor: '#fff3e0',
        fontWeight: 500,
        border: '1px solid #ffcc80',
    },
    groupLabel: {
        fontWeight: 600,
        fontSize: 14,
        color: '#333',
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
    },
}));

const DialogTitle = withStyles(styles)(props => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Box display="flex" alignItems="center">
                <i className="fas fa-hotel" style={{fontSize: 28, marginRight: 12}} />
                <Typography variant="h6" style={{fontWeight: 700, fontSize: 20, color: "#fff"}}>
                    {children}
                </Typography>
            </Box>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles(theme => ({
    root: {
        padding: theme.spacing(3),
        backgroundColor: '#fafafa',
    },
}))(MuiDialogContent);


const UpdateOrCreateCompetitorAnalysisModalAdvanced = ({
    allDays,
    getCpa,
    type,
    onHotelAdded,
    currency,
    propertyProducts,
    propertyList,
    propertyListFetching,
    isOpen: externalIsOpen,
    onClose: externalOnClose,
    onSave: externalOnSave,
    onGroupChange,
}) =>  {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const classes = useStyles();

    const { syncCompetitor, errorSyncCompetitor } = useSelector(props => props?.cpaReducer);
    const {
        cpaGroup,
        cpaGroupFetching,
        cpaGroupSync,
        cpaGroupSyncFetching,
        cpaGroupSyncError,
        cpaGroupMapping,
        cpaGroupMappingFetching,
        cpaGroupMappingSync,
        cpaGroupMappingSyncFetching,
        cpaGroupMappingSyncError,
    } = useSelector(state => state.cpaGroupReducer);

    // Internal open state (ilk açılış butonu için)
    const [internalIsOpen, setInternalIsOpen] = useState(false);

    // External veya internal open kontrolü
    const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

    // Otel seçimi state
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [saving, setSaving] = useState(false);

    // Grup state
    const [selectedGroupId, setSelectedGroupId] = useState("");
    const [newGroupName, setNewGroupName] = useState("");
    const [editGroupId, setEditGroupId] = useState(null);
    const [editGroupName, setEditGroupName] = useState("");
    const [showNewGroupInput, setShowNewGroupInput] = useState(false);
    const [groupMappingHotels, setGroupMappingHotels] = useState([]);
    const [groupMappingSaving, setGroupMappingSaving] = useState(false);
    const [groupSaveSuccess, setGroupSaveSuccess] = useState(false);
    const [mappingSaveSuccess, setMappingSaveSuccess] = useState(false);

    const propertyId = getProperty();
    const isGroupMode = selectedGroupId !== "" && selectedGroupId !== null;

    // Mevcut extranetwork competitor'ları
    const existingExtranetworkIds = useMemo(() => {
        return (getCpa || [])
            .filter(c => c?.competitor_property_source === "extranetwork")
            .map(c => parseInt(c?.key));
    }, [getCpa]);

    // Kullanılabilir property listesi
    const availableProperties = useMemo(() => {
        return (propertyList || []).filter(p => p != null);
    }, [propertyList]);

    // Grup mapping için kullanılabilir oteller (getCpa'daki tüm oteller)
    const availableHotelsForMapping = useMemo(() => {
        if (!propertyList || !Array.isArray(propertyList)) return [];
        return propertyList
            .filter(p => p != null)
            .map(p => ({
                id: p.id,
                property_id: parseInt(getProperty()),
                name: p.name,
                key: String(p.id),
                competitor_property_source: "extranetwork",
                default_photo: p.default_photo,
            }));
    }, [propertyList]);

    // Seçili grubun adı
    const selectedGroupName = useMemo(() => {
        if (!selectedGroupId || !cpaGroup) return '';
        const found = cpaGroup.find(g => g.id === selectedGroupId);
        return found?.name || '';
    }, [selectedGroupId, cpaGroup]);

    // Modal açılınca
    const handleOpen = useCallback(() => {
        if (externalIsOpen === undefined) {
            setInternalIsOpen(true);
        }
        dispatch(getCpaGroup(propertyId));

        // Mevcut competitor'ları pre-select
        const preSelected = (propertyList || []).filter(p => existingExtranetworkIds.includes(p?.id));
        setSelectedProperties(preSelected);
    }, [dispatch, propertyId, propertyList, existingExtranetworkIds, externalIsOpen]);

    // Modal kapanınca
    const handleClose = useCallback(() => {
        if (externalOnClose) {
            externalOnClose();
        } else {
            setInternalIsOpen(false);
        }
        setSelectedGroupId("");
        setNewGroupName("");
        setEditGroupId(null);
        setEditGroupName("");
        setShowNewGroupInput(false);
        setGroupSaveSuccess(false);
        setMappingSaveSuccess(false);
        setGroupMappingHotels([]);
        setSaving(false);
    }, [externalOnClose]);

    // External open değiştiğinde state'leri güncelle
    useEffect(() => {
        if (externalIsOpen === true) {
            dispatch(getCpaGroup(propertyId));
            const preSelected = (propertyList || []).filter(p => existingExtranetworkIds.includes(p?.id));
            setSelectedProperties(preSelected);
        }
    }, [externalIsOpen, dispatch, propertyId, propertyList, existingExtranetworkIds]);

    // Otel kaydet
    const handlePropertySave = () => {
        if (!selectedProperties || selectedProperties?.length === 0) return;
        setSaving(true);

        // Mevcut non-extranetwork competitor'ları koru
        const existingNonExtranetwork = (getCpa || []).filter(
            c => c?.competitor_property_source !== "extranetwork"
        );

        // Seçilen property'leri extranetwork formatına dönüştür
        const extranetworkCompetitors = selectedProperties.map(p => ({
            id: p?.id,
            property_id: parseInt(getProperty()),
            name: p?.name,
            key: String(p?.id),
            competitor_property_source: "extranetwork",
        }));

        const allCompetitors = [...existingNonExtranetwork, ...extranetworkCompetitors];

        dispatch(setSyncCpaCompetitorClearState());
        dispatch(setSyncCpaCompetitor(allCompetitors));

        if (externalOnSave) {
            externalOnSave(allCompetitors);
        }
    };

    // Sync başarılı
    useEffect(() => {
        if (syncCompetitor?.status === 200 && saving) {
            setSaving(false);
            dispatch(getPropertyCpa());
            handleClose();
        }
    }, [syncCompetitor, saving]);

    // Modal kapanınca clear
    useEffect(() => {
        if (!isOpen) {
            dispatch(setSyncCpaCompetitorClearState());
            dispatch(getCpaClearState());
        }
    }, [dispatch, isOpen]);

    // Grup seçildiğinde mapping'i çek
    useEffect(() => {
        if (selectedGroupId) {
            dispatch(getCpaGroupMapping({ property_id: propertyId, competitor_group_id: selectedGroupId }));
        } else {
            setGroupMappingHotels([]);
        }
    }, [selectedGroupId, dispatch, propertyId]);

    // Mapping verisi geldiğinde -> getCpa'daki oteller ile eşleştir
    useEffect(() => {
        if (cpaGroupMapping && Array.isArray(cpaGroupMapping) && selectedGroupId) {
            const matched = [];
            const matchedIds = new Set();

            cpaGroupMapping.forEach(mapping => {
                const mappingKey = String(mapping?.competitor_property_key);
                const mappingSource = mapping?.competitor_property_source;

                // 1. Extranetwork source: key = propertyList id ile eşleştir
                if (mappingSource === "extranetwork") {
                    const found = availableHotelsForMapping.find(h => h.key === mappingKey);
                    if (found && !matchedIds.has(found.key)) {
                        matched.push(found);
                        matchedIds.add(found.key);
                    }
                    return;
                }

                // 2. Non-extranetwork (tripadvisor vb.): getCpa'dan ismi bul, propertyList'te eşleştir
                const cpaHotel = (getCpa || []).find(c => String(c?.key) === mappingKey);
                if (cpaHotel) {
                    const cpaName = (cpaHotel?.name || '').trim().toLowerCase();
                    // İsim bazlı propertyList'te bul
                    const foundByName = availableHotelsForMapping.find(h =>
                        (h?.name || '').trim().toLowerCase() === cpaName && !matchedIds.has(h.key)
                    );
                    if (foundByName) {
                        matched.push(foundByName);
                        matchedIds.add(foundByName.key);
                        return;
                    }

                    // PropertyList'te bulunamadı - extranetwork key'i ile getCpa'da aynı isimli otel var mı?
                    const extranetworkVersion = (getCpa || []).find(c =>
                        c?.competitor_property_source === "extranetwork" &&
                        (c?.name || '').trim().toLowerCase() === cpaName
                    );
                    if (extranetworkVersion) {
                        const found = availableHotelsForMapping.find(h => h.key === String(extranetworkVersion.key));
                        if (found && !matchedIds.has(found.key)) {
                            matched.push(found);
                            matchedIds.add(found.key);
                            return;
                        }
                    }
                }
            });

            setGroupMappingHotels(matched);
        }
    }, [cpaGroupMapping, selectedGroupId, availableHotelsForMapping, getCpa]);

    // Grup sync başarılı olduğunda
    useEffect(() => {
        if (cpaGroupSync && !cpaGroupSyncFetching && !cpaGroupSyncError) {
            setGroupSaveSuccess(true);
            setNewGroupName("");
            setShowNewGroupInput(false);
            setEditGroupId(null);
            setEditGroupName("");
            dispatch(getCpaGroup(propertyId));
            setTimeout(() => setGroupSaveSuccess(false), 3000);
        }
    }, [cpaGroupSync, cpaGroupSyncFetching]);

    // Mapping sync başarılı olduğunda
    useEffect(() => {
        if (cpaGroupMappingSync && !cpaGroupMappingSyncFetching && !cpaGroupMappingSyncError) {
            setMappingSaveSuccess(true);
            setGroupMappingSaving(false);
            dispatch(getCpaGroupMapping({ property_id: propertyId, competitor_group_id: selectedGroupId }));
            setTimeout(() => setMappingSaveSuccess(false), 3000);
        }
    }, [cpaGroupMappingSync, cpaGroupMappingSyncFetching]);

    // Grup kaydet/güncelle
    const handleGroupSave = () => {
        if (editGroupId) {
            dispatch(syncCpaGroup({ property_id: parseInt(propertyId), name: editGroupName, id: editGroupId }));
        } else if (newGroupName.trim()) {
            dispatch(syncCpaGroup({ property_id: parseInt(propertyId), name: newGroupName.trim() }));
        }
    };

    // Grup mapping kaydet
    const handleGroupMappingSave = () => {
        if (!selectedGroupId) return;
        setGroupMappingSaving(true);
        const competitor = groupMappingHotels.map(hotel => ({
            source: "extranetwork",
            key: String(hotel?.id),
        }));
        dispatch(syncCpaGroupMapping({
            property_id: parseInt(propertyId),
            competitor_group_id: selectedGroupId,
            competitor,
        }));
    };

    // Edit iptal
    const handleCancelEdit = () => {
        setEditGroupId(null);
        setEditGroupName("");
        setShowNewGroupInput(false);
        setNewGroupName("");
    };

    return (
        <>
            {/* Kendi butonu (sadece external kontrolü yoksa göster) */}
            {externalIsOpen === undefined && (
                <ButtonAtm
                    className={type === "edit" ? 'inventory-action-btn bg-dark-blue inventory-filter-box' : "filter-action-button cpa-new-property-btn"}
                    buttontext={t('enw-cpa_add-hotel')}
                    fullwidth={false}
                    onClick={handleOpen}
                    startIcon={<i style={{fontSize: "12px", marginTop: "2px"}} className="fas fa-plus" />}
                />
            )}

            <Dialog
                onClose={handleClose}
                disableBackdropClick={true}
                aria-labelledby="competitor-modal"
                open={isOpen}
                className={classes.dialog}
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 1300 }}
            >
                <DialogTitle
                    id="competitor-modal"
                    onClose={handleClose}
                >
                    {t('enw-cpa_add-hotel-modal-title')}
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>

                    {/* ===== Alerts ===== */}
                    {syncCompetitor?.status === 200 && (
                        <Alert severity="success" className={classes.successAlert}>
                            {t('enw-cpa_add-hotel-success-message')}
                        </Alert>
                    )}
                    {isDefined(errorSyncCompetitor?.response) && (
                        <Alert severity="error" className={classes.errorAlert}>
                            {errorSyncCompetitor?.response?.data?.message}
                        </Alert>
                    )}
                    {groupSaveSuccess && (
                        <Alert severity="success" className={classes.successAlert}>
                            {'Grup başarıyla kaydedildi.'}
                        </Alert>
                    )}
                    {mappingSaveSuccess && (
                        <Alert severity="success" className={classes.successAlert}>
                            {'Grup otelleri başarıyla kaydedildi.'}
                        </Alert>
                    )}
                    {cpaGroupSyncError && (
                        <Alert severity="error" className={classes.errorAlert}>
                            {cpaGroupSyncError?.response?.data?.message || t('enw-error-message')}
                        </Alert>
                    )}
                    {cpaGroupMappingSyncError && (
                        <Alert severity="error" className={classes.errorAlert}>
                            {cpaGroupMappingSyncError?.response?.data?.message || t('enw-error-message')}
                        </Alert>
                    )}

                    {/* ===== Selected Count ===== */}
                    <Box className={classes.selectedCount}>
                        <Box display="flex" alignItems="center">
                            <i className="fas fa-building" style={{ color: '#667eea', marginRight: 8 }} />
                            <Typography style={{ fontWeight: 500, color: '#444' }}>
                                {t('enw-cpa-selected_hotels')}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <span className={classes.countBadge}>
                                {isGroupMode ? (groupMappingHotels?.length || 0) : (selectedProperties?.length || 0)}
                            </span>
                        </Box>
                    </Box>

                    {/* ===== Grup Yönetimi ===== */}
                    <Box className={classes.groupSection}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography className={classes.groupLabel}>
                                <i className="fas fa-layer-group" style={{ marginRight: 8, color: '#667eea' }} />
                                {'Grup Yönetimi'}
                            </Typography>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                {!showNewGroupInput && (
                                    <ButtonAtm
                                        className="bg-dark-blue"
                                        buttontext={'Yeni Grup'}
                                        onClick={() => {
                                            setShowNewGroupInput(true);
                                            setEditGroupId(null);
                                            setEditGroupName("");
                                            setNewGroupName("");
                                        }}
                                        startIcon={<i className="fas fa-plus" style={{ fontSize: 12, color: "#fff" }} />}
                                        style={{ padding: '6px 14px', fontSize: 12, borderRadius: 8, minWidth: 0, color: "#fff", fontWeight: 600 }}
                                    />
                                )}
                                {selectedGroupId && (
                                    <ButtonAtm
                                        className="bg-dark-orange"
                                        startIcon={<i className="fas fa-pen" style={{ fontSize: 12 }} />}
                                        onClick={() => {
                                            const group = cpaGroup.find(g => g?.id === selectedGroupId);
                                            if (group) {
                                                setEditGroupId(group?.id);
                                                setEditGroupName(group?.name);
                                                setShowNewGroupInput(true);
                                            }
                                        }}
                                        style={{ padding: '6px 14px', fontSize: 12, borderRadius: 8, minWidth: 0, color: "#fff", fontWeight: 600 }}
                                        buttontext={'Düzenle'}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Yeni Grup / Düzenle Input */}
                        {showNewGroupInput && (
                            <Box className={classes.newGroupInput}>
                                <TextField
                                    label={'Grup Adı'}
                                    variant="outlined"
                                    size="small"
                                    value={editGroupId ? editGroupName : newGroupName}
                                    onChange={(e) => editGroupId ? setEditGroupName(e.target.value) : setNewGroupName(e.target.value)}
                                    style={{ flex: 1, paddingRight: "10px" }}
                                    InputProps={{ style: { borderRadius: 10 } }}
                                />
                                <ButtonAtm
                                    className="bg-green"
                                    buttontext={
                                        cpaGroupSyncFetching
                                            ? <CircularProgress size={16} style={{ color: '#fff' }} />
                                            : editGroupId
                                                ? (t('btn-update') || 'Güncelle')
                                                : (t('btn-save') || 'Kaydet')
                                    }
                                    onClick={handleGroupSave}
                                    disabled={cpaGroupSyncFetching || (editGroupId ? !editGroupName.trim() : !newGroupName.trim())}
                                    style={{ padding: '6px 20px', fontSize: 13, borderRadius: 8, minWidth: 0, marginTop: 24, height: "40px", fontWeight: 600, color: "#fff" }}
                                />
                                <IconButton size="medium" onClick={handleCancelEdit} style={{ marginLeft: 0, marginTop: 22 }}>
                                    <CloseIcon style={{ fontSize: 18 }} />
                                </IconButton>
                            </Box>
                        )}

                        {/* Grup Select */}
                        <FormControl variant="outlined" size="small" className={classes.groupSelectControl}>
                            <InputLabel style={{ fontWeight: 500 }}>
                                {'Grup Seç'}
                            </InputLabel>
                            <Select
                                value={selectedGroupId}
                                onChange={(e) => {
                                    const newGroupId = e.target.value;
                                    setSelectedGroupId(newGroupId);
                                    setShowNewGroupInput(false);
                                    setEditGroupId(null);
                                    setEditGroupName("");
                                    setNewGroupName("");
                                    setMappingSaveSuccess(false);
                                    if (onGroupChange) {
                                        onGroupChange(newGroupId);
                                    }
                                }}
                                label="Grup Seç"
                                disabled={cpaGroupFetching}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return (
                                            <span style={{ fontStyle: 'italic'}}>
                                                Tüm Oteller (Grupsuz)
                                            </span>
                                        );
                                    }

                                    const group = cpaGroup?.find(g => g.id === selected);

                                    return (
                                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                            {/* <i
                                                className="fas fa-folder"
                                                style={{ fontSize: 12, color: '#f57c00' }}
                                            /> */}
                                            {group?.name}
                                        </Box>
                                    );
                                }}
                                startAdornment={
                                    <i
                                        className="fas fa-layer-group"
                                        style={{
                                            color: '#667eea',
                                            marginRight: 8,
                                            marginLeft: 4,
                                            fontSize: 13
                                        }}
                                    />
                                }
                            >
                                <MenuItem value="">
                                    <em>Tüm Oteller (Grupsuz)</em>
                                </MenuItem>

                                {cpaGroup?.map((group) => (
                                    <MenuItem key={group?.id} value={group?.id}>
                                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                            {/* <i
                                                className="fas fa-folder"
                                                style={{ fontSize: 12, color: '#f57c00' }}
                                            /> */}
                                            {group?.name}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* ===== Grup Seçili: Grup Otellerini Düzenle ===== */}
                    {isGroupMode && (
                        <>
                            <Box mb={1}>
                                <Typography className={classes.groupLabel} style={{ fontSize: 13, color: '#555' }}>
                                    <i className="fas fa-link" style={{ marginRight: 8, color: '#f57c00', fontSize: 13 }} />
                                    {'Grup Otelleri'}
                                    <span style={{ fontWeight: 400, fontSize: 12, color: '#888', marginLeft: 8 }}>
                                        ({selectedGroupName})
                                    </span>
                                </Typography>
                            </Box>

                            {cpaGroupMappingFetching ? (
                                <Box display="flex" justifyContent="center" py={3}>
                                    <CircularProgress size={24} style={{ color: '#f57c00' }} />
                                </Box>
                            ) : (
                                <Grid container>
                                    <Grid item xs={12} className={classes.autocompleteWrapper}>
                                        <Autocomplete
                                            multiple
                                            options={availableHotelsForMapping}
                                            getOptionLabel={(option) => option?.name || ''}
                                            getOptionSelected={(option, value) => {
                                                return String(option?.id) === String(value?.id);
                                            }}
                                            value={groupMappingHotels}
                                            onChange={(event, value) => setGroupMappingHotels(value)}
                                            filterSelectedOptions
                                            disableCloseOnSelect
                                            className={classes.autocomplete}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        key={option?.key || option?.id}
                                                        label={option?.name}
                                                        {...getTagProps({ index })}
                                                        className={classes.groupMappingChip}
                                                        deleteIcon={<CloseIcon style={{ fontSize: 16 }} />}
                                                    />
                                                ))
                                            }
                                            renderOption={(option) => (
                                                <Box display="flex" alignItems="center" style={{ padding: '4px 0' }}>
                                                    <i className="fas fa-hotel" style={{ fontSize: 12, marginRight: 10, color: '#667eea' }} />
                                                    <Typography style={{ fontWeight: 500, fontSize: 14, color: '#333' }}>
                                                        {option?.name}
                                                    </Typography>
                                                </Box>
                                            )}
                                            PaperComponent={({ children, ...paperProps }) => (
                                                <Paper {...paperProps} style={{ borderRadius: 12, marginTop: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                                                    {children}
                                                </Paper>
                                            )}
                                            noOptionsText={
                                                <Typography style={{ color: '#999', padding: 16, textAlign: 'center' }}>
                                                    {'Otel bulunamadı'}
                                                </Typography>
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label={'Gruba otel ekle / çıkar'}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <i className="fas fa-search" style={{ color: '#999', marginRight: 8, marginLeft: 4 }} />
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} style={{ textAlign: 'center' }}>
                                        <ButtonAtm
                                            className={`room-rate-modal-save room-rate-save bg-red m-auto ${classes.submitButton}`}
                                            buttontext={
                                                groupMappingSaving || cpaGroupMappingSyncFetching
                                                    ? <CircularProgress size={16} style={{ color: '#fff' }} />
                                                    : (t('btn-save') || 'Kaydet')
                                            }
                                            onClick={handleGroupMappingSave}
                                            disabled={!selectedGroupId || groupMappingSaving || cpaGroupMappingSyncFetching}
                                            startIcon={<i className="fas fa-check" style={{ marginRight: 4 }} />}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </>
                    )}

                    {/* ===== Grup Seçili Değil: Property List ile Otel Ekleme ===== */}
                    {!isGroupMode && (
                        <>
                            {propertyListFetching ? (
                                <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
                                    <CircularProgress size={36} style={{ color: '#d71f26' }} />
                                </Box>
                            ) : (
                                <>
                                    <Autocomplete
                                        multiple
                                        options={availableProperties}
                                        getOptionLabel={(option) => option && option?.name ? option?.name : ""}
                                        getOptionSelected={(option, value) => option && value && option?.id === value?.id}
                                        value={selectedProperties}
                                        onChange={(event, value) => setSelectedProperties(value)}
                                        filterSelectedOptions
                                        disableCloseOnSelect
                                        className={classes.autocomplete}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    key={option?.id}
                                                    label={option?.name}
                                                    {...getTagProps({ index })}
                                                    className={classes.chip}
                                                    avatar={
                                                        option?.default_photo ? (
                                                            <img
                                                                src={option?.default_photo}
                                                                alt={option?.name}
                                                                style={{ width: 24, height: 24, borderRadius: 4 }}
                                                            />
                                                        ) : undefined
                                                    }
                                                    deleteIcon={<CloseIcon style={{ fontSize: 16 }} />}
                                                />
                                            ))
                                        }
                                        renderOption={(option) => (
                                            <Box display="flex" alignItems="center" style={{ padding: '4px 0' }}>
                                                {option?.default_photo && (
                                                    <img
                                                        src={option?.default_photo}
                                                        alt={option?.name}
                                                        style={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: 8,
                                                            marginRight: 12,
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                )}
                                                <Typography style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>
                                                    {option?.name}
                                                </Typography>
                                            </Box>
                                        )}
                                        PaperComponent={({ children, ...paperProps }) => (
                                            <Paper {...paperProps} style={{ borderRadius: 12, marginTop: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                                                {children}
                                            </Paper>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                label={t('enw-cpa_add-hotel-label')}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <i className="fas fa-search" style={{ color: '#999', marginRight: 8, marginLeft: 4 }} />
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        style={{ marginBottom: 24 }}
                                    />

                                    <Box style={{ textAlign: 'center' }}>
                                        <ButtonAtm
                                            className={`room-rate-modal-save room-rate-save bg-red m-auto ${classes.submitButton}`}
                                            buttontext={saving ? t('enw-payment-manual_payment-loading-message') : t('btn-save')}
                                            onClick={handlePropertySave}
                                            disabled={saving || !selectedProperties || selectedProperties.length === 0}
                                            startIcon={saving
                                                ? <CircularProgress size={16} style={{ color: '#fff' }} />
                                                : <i className="fas fa-check" style={{ marginRight: 4 }} />
                                            }
                                        />
                                    </Box>
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default UpdateOrCreateCompetitorAnalysisModalAdvanced;